import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CompanyDetails } from "../types/companyTypes";

export interface ExtractedCompanyData {
  status: string;
  company_name: string;
  website: string;
  industry: string;
  products_services: string;
  overview: string;
  location: string;
  address: string;
  employee_count: string;
  confidence: string;
  token_usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_cost: number;
  };
}

export interface CompanyState {
  details: CompanyDetails | null;
  recruiterDetails: CompanyDetails | null;
  extractedData: ExtractedCompanyData | null;
  loading: boolean;
  error: string | null;
}

const initialState: CompanyState = {
  details: null,
  recruiterDetails: null,
  extractedData: null,
  loading: false,
  error: null,
};

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    // EXISTING FUNCTIONS - Keep unchanged for jobseeker/general use
    setCompanyDetails: (
      state: CompanyState,
      action: PayloadAction<CompanyDetails>
    ) => {
      state.details = action.payload as CompanyDetails;
    },
    updateCompanyDetails: (
      state: CompanyState,
      action: PayloadAction<Partial<CompanyDetails>>
    ) => {
      if (state.details === null) {
        state.details = action.payload as CompanyDetails;
      } else {
        state.details = { ...state.details, ...action.payload };
      }
    },
    updateCompanyGallery: (
      state: CompanyState,
      action: PayloadAction<string[]>
    ) => {
      if (state.details) {
        state.details.file = action.payload;
      }
    },
    setCompanyLoading: (
      state: CompanyState,
      action: PayloadAction<boolean>
    ) => {
      state.loading = action.payload;
    },
    setCompanyError: (state: CompanyState, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    resetCompany: (state: CompanyState) => {
      state.details = initialState.details;
    },

    // NEW FUNCTIONS - For recruiter-specific operations
    setCompanyRecruiterDetails: (
      state: CompanyState,
      action: PayloadAction<CompanyDetails>
    ) => {
      state.recruiterDetails = action.payload as CompanyDetails;
    },
    updateCompanyRecruiterDetails: (
      state: CompanyState,
      action: PayloadAction<Partial<CompanyDetails>>
    ) => {
      if (state.recruiterDetails === null) {
        state.recruiterDetails = action.payload as CompanyDetails;
      } else {
        state.recruiterDetails = { ...state.recruiterDetails, ...action.payload };
      }
    },
    resetCompanyRecruiter: (state: CompanyState) => {
      state.recruiterDetails = initialState.recruiterDetails;
    },
    setExtractedCompanyData: (
      state: CompanyState,
      action: PayloadAction<ExtractedCompanyData>
    ) => {
      state.extractedData = action.payload;
    },
    clearExtractedCompanyData: (state: CompanyState) => {
      state.extractedData = null;
    },
  },
});

export const {
  // EXISTING EXPORTS - unchanged
  setCompanyDetails,
  updateCompanyDetails,
  setCompanyLoading,
  updateCompanyGallery,
  setCompanyError,
  resetCompany,

  // NEW EXPORTS - for recruiter functionality
  setCompanyRecruiterDetails,
  updateCompanyRecruiterDetails,
  resetCompanyRecruiter,

  // NEW EXPORTS - for extracted company data
  setExtractedCompanyData,
  clearExtractedCompanyData,
} = companySlice.actions;

export default companySlice.reducer;